import * as Sentry from "@sentry/react";

// Sentry project DSN. A DSN is a public client key (it ships in the browser
// bundle by design), so it is safe to keep here as a hardcoded fallback. This
// guards against the failure we already hit once: the VITE_SENTRY_DSN env var
// going missing in Vercel and silently disabling all error monitoring. The env
// var still takes precedence when set (e.g. to point a staging build at a
// different Sentry project).
const SENTRY_DSN =
  import.meta.env.VITE_SENTRY_DSN ||
  "https://459e19973f13bec608096adefb2c7f17@o4511178494312448.ingest.de.sentry.io/4511179919196240";

// Secrets travel in this app's URLs: a review, share or invite link IS its token, and some links
// carry a credential in the query or fragment. A URL reaches Sentry in almost everything it sends
// (the page URL and Referer, breadcrumbs, span names, error messages, replays), so every payload
// is scrubbed with these patterns (the backend does the same for its logs, `redact_url_secrets`).
// Each pattern also matches the URL percent-encoded inside another URL's query, which is how a
// link survives a sign-in detour: `/?redirect=%2Frights%2Finvite%2F<token>`,
// `/onboarding?returnTo=%2Freview%2F<token>`.
//
// Every pattern must run in linear time: this runs on the page URL before first paint
// (`shouldRecordReplay`), so a crafted link must not freeze the tab. No pattern may search forward
// from each place it starts across text another start also covers (that backtracking was
// quadratic); `src/test/sentry-url-secrets.test.ts` times them on 1 MB adversarial strings.
const SLASH = String.raw`(?:/|%2F)`;
const EQUALS = String.raw`(?:=|%3D)`;
// A token is one path segment: it ends where its URL part ends, or where the string (or the JSON)
// holding the URL does.
const TOKEN = String.raw`[^/?#&"'\s\\]+`;
// An id in front of a token or query (a playlist's or track's UUID). It cannot hold `%`, so it ends
// at the first separator, encoded or not.
const ID = String.raw`[^/?#&%"'\s\\]+`;
// A storefront slug in front of its token: the shortest run up to the next separator, so a slug
// holding `%C3%A9` still finds its token. A failed run ends at its first `%2F` (one followed by a
// token would have matched), so it never crosses into the next link.
const SLUG = String.raw`[^/?#&"'\s\\]+?`;
// A query or fragment value runs to the next parameter (a password may contain `/`).
const VALUE = String.raw`[^&#"'\s\\]+`;

const URL_SECRETS: readonly RegExp[] = [
  // A project review link, and the API behind it (`/api/review/:token`, `/feedback`).
  `${SLASH}review${SLASH}`,
  // Playlist share links and collaborator invites.
  `${SLASH}playlist${SLASH}(?:share|invite)${SLASH}`,
  // A rights-holder invite, and the API that reads and accepts it.
  `${SLASH}rights${SLASH}invite${SLASH}`,
  `${SLASH}rights-holder-invites${SLASH}`,
  // A reference playlist's share link (`/reference-imports/share/:token`), and its revoke call.
  `${SLASH}reference-imports${SLASH}(?:playlists${SLASH}${ID}${SLASH})?share${SLASH}`,
  // A storefront link, public or the owner's: the slug stays, the token after it goes.
  `${SLASH}(?:storefront${SLASH}share|marketplace${SLASH}storefront)${SLASH}${SLUG}${SLASH}`,
].map((prefix) => new RegExp(`(${prefix})${TOKEN}`, "gi")).concat([
  // Credentials in a query or fragment: share and invite tokens (`invitation_token` also as a
  // PostgREST filter), a shared playlist's password, the session tokens of a Supabase
  // password-reset or sign-up redirect, and the token of a Supabase Storage signed URL.
  new RegExp(
    String.raw`((?:[?#&]|%3F|%23|%26)(?:share_token|invitation_token|rights_invite|password|access_token|refresh_token|token)${EQUALS})${VALUE}`,
    "gi"
  ),
]);

// A shared track's video link (`/track/shared/:id?v=<token>`). Its whole query is matched first
// (to the fragment, or the end of the URL), then every `v` parameter in it is redacted, so each
// character is read once. Every other `v=` (a YouTube link) is not a secret and stays.
const TRACK_QUERY = new RegExp(
  String.raw`(${SLASH}track${SLASH}shared${SLASH}${ID}(?:\?|%3F))([^#"'\s\\]*)`,
  "gi"
);
const VIDEO_PARAM = new RegExp(String.raw`(^|[?&]|%3F|%26)(v${EQUALS})${VALUE}`, "gi");

/** `text` with every URL secret replaced by `[redacted]`; the rest of the URL is kept. */
export function redactUrlSecrets(text: string): string {
  return URL_SECRETS.reduce(
    (redacted, pattern) => redacted.replace(pattern, "$1[redacted]"),
    text
  ).replace(TRACK_QUERY, (_link, path: string, query: string) =>
    path + query.replace(VIDEO_PARAM, "$1$2[redacted]")
  );
}

/**
 * Whether a page may be recorded by session replay. Not when it opens on a secret link (a review,
 * share or invite link, or a URL carrying a token or password), nor on the password-reset page: a
 * replay records the page itself (its URL, its DOM), which the event hooks below cannot fully
 * scrub. Errors and traces are still reported from those pages, scrubbed.
 */
export function shouldRecordReplay({
  pathname,
  search,
  hash,
}: Pick<Location, "pathname" | "search" | "hash">): boolean {
  if (pathname.startsWith("/auth/update-password")) return false;
  const url = `${pathname}${search}${hash}`;
  return redactUrlSecrets(url) === url;
}

/**
 * `value` with every URL secret in it redacted, scanned as one JSON string so no field is missed;
 * null when it cannot be serialised, so the caller decides what can still be sent. Unchanged values
 * are returned as they are. An event's `sdkProcessingMetadata` is carried over unscanned: it is the
 * SDK's own bookkeeping (it holds scope objects that may not serialise) and is deleted before an
 * event is sent.
 */
function redactedCopy<T extends object>(value: T): T | null {
  const { sdkProcessingMetadata, ...sent } = value as T & { sdkProcessingMetadata?: unknown };
  try {
    const json = JSON.stringify(sent);
    const redacted = redactUrlSecrets(json);
    if (redacted === json) return value;
    const copy = JSON.parse(redacted) as T;
    return sdkProcessingMetadata === undefined ? copy : { ...copy, sdkProcessingMetadata };
  } catch {
    return null;
  }
}

/**
 * An error event that could not be serialised to scan: it still reports what failed and where,
 * with its message redacted, but every free-form part that can hold a URL (the page URL and
 * Referer, breadcrumbs, extra data, contexts) is dropped rather than sent unscanned.
 */
function errorWithoutUrls(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const kept: Sentry.ErrorEvent = {
    ...event,
    message: event.message && redactUrlSecrets(event.message),
    transaction: event.transaction && redactUrlSecrets(event.transaction),
    exception: event.exception && {
      ...event.exception,
      values: event.exception.values?.map((exception) => ({
        ...exception,
        value: exception.value && redactUrlSecrets(exception.value),
      })),
    },
  };
  delete kept.request;
  delete kept.breadcrumbs;
  delete kept.extra;
  delete kept.contexts;
  delete kept.logentry;
  return kept;
}

// Initialize Sentry
export function initSentry() {
  // Skip Sentry initialization in development
  if (import.meta.env.MODE === "development") {
    console.log("Sentry disabled in development mode");
    return;
  }

  // Loud failure instead of a silent no-op if the DSN is ever truly absent.
  if (!SENTRY_DSN) {
    console.error(
      "[Sentry] No DSN configured — frontend error monitoring is DISABLED."
    );
    return;
  }

  const replay = shouldRecordReplay(window.location)
    ? Sentry.replayIntegration({
        // A link's target can be a share link (the storefront cards on /home link to the
        // owner's storefront URL, token included), so `href` joins Sentry's default masked
        // attributes. Text, inputs and media are already masked or blocked by default.
        maskAttributes: ["title", "placeholder", "aria-label", "href"],
        // Only the replay's breadcrumbs and network/navigation spans pass through here (not its
        // DOM snapshots); one that cannot be scanned is dropped rather than recorded.
        beforeAddRecordingEvent: redactedCopy,
      })
    : null;

  const client = Sentry.init({
    dsn: SENTRY_DSN,
    environment: import.meta.env.MODE,
    integrations: replay
      ? [Sentry.browserTracingIntegration(), replay]
      : [Sentry.browserTracingIntegration()],
    // Performance Monitoring
    tracesSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
    // Session Replay
    replaysSessionSampleRate: import.meta.env.MODE === "production" ? 0.1 : 1.0,
    replaysOnErrorSampleRate: 1.0,

    beforeSend(event) {
      // Chunk load failures used to be DISCARDED here as "not actionable". They
      // are the opposite: a lazy chunk that 404s (the usual cause being a tab
      // holding an index.html from a superseded deploy) is a user staring at a
      // blank page. Dropping them at source meant the one class of blank-page
      // error that does throw was the one class Sentry never saw. They are kept
      // and tagged instead, so they can be grouped or muted in the Sentry UI —
      // a decision that belongs there, where the volume is visible, not here.
      if (event.exception) {
        const error = event.exception.values?.[0];
        if (
          error?.type === "ChunkLoadError" ||
          error?.value?.includes("Loading chunk") ||
          error?.value?.includes("Loading CSS chunk")
        ) {
          event.tags = { ...event.tags, chunk_load_failure: "true" };
        }
      }

      // Runs after Sentry has normalised the event (breadcrumbs included), so it serialises.
      return redactedCopy(event) ?? errorWithoutUrls(event);
    },

    // Set user context
    beforeSendTransaction(event) {
      // Add custom tags for better filtering
      event.tags = {
        ...event.tags,
        component: "frontend",
        version: import.meta.env.VITE_APP_VERSION || "unknown",
      };
      // A transaction that cannot be scanned is dropped: losing one trace is cheaper than a token.
      return redactedCopy(event);
    },

    // Standalone spans (the INP web vital, named after the page's route) never pass through
    // beforeSendTransaction. A span must be returned (null keeps the original), so one that cannot
    // be scanned loses its attributes and links instead.
    beforeSendSpan(span) {
      return (
        redactedCopy(span) ?? {
          ...span,
          description: span.description && redactUrlSecrets(span.description),
          data: {},
          links: undefined,
        }
      );
    },
  });

  if (client && replay) {
    // The replay's own event (the URLs it visited, the page URL and Referer) passes through none
    // of the hooks above. Its core breadcrumbs are removed first, as Replay itself does: here they
    // are still raw (not yet normalised) and may not serialise.
    client.addEventProcessor((event) => {
      if (event.type !== "replay_event") return event;
      delete event.breadcrumbs;
      return redactedCopy(event);
    });
    // A secret link opened inside the app (a seller opening their own storefront) ends the
    // recording for the rest of the page's life: a replay's page snapshots carry the page URL,
    // which no hook can scrub. Only the path is known here, before the URL changes.
    client.on("startNavigationSpan", ({ name }) => {
      if (!shouldRecordReplay({ pathname: name, search: "", hash: "" })) void replay.stop();
    });
  }
}

// Helper function to set user context
export function setSentryUser(user: {
  id: string;
  email?: string;
  username?: string;
}) {
  if (import.meta.env.MODE === "development") {
    console.log("Sentry setUser called in development mode (disabled)");
    return;
  }

  Sentry.setUser({
    id: user.id,
    email: user.email,
    username: user.username,
  });
}

// Helper function to add breadcrumbs
export function addSentryBreadcrumb(
  message: string,
  category?: string,
  level?: Sentry.SeverityLevel
) {
  if (import.meta.env.MODE === "development") {
    console.log("Sentry breadcrumb in development mode (disabled):", message);
    return;
  }

  Sentry.addBreadcrumb({
    message,
    category: category || "user",
    level: level || "info",
    timestamp: Date.now() / 1000,
  });
}

// Helper function to capture exceptions
export function captureSentryException(
  error: Error,
  context?: Record<string, any>
) {
  if (import.meta.env.MODE === "development") {
    console.log(
      "Sentry exception in development mode (disabled):",
      error.message
    );
    return;
  }

  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("additional_info", context);
    }
    Sentry.captureException(error);
  });
}

// Helper function to capture messages
export function captureSentryMessage(
  message: string,
  level: Sentry.SeverityLevel = "info",
  context?: Record<string, any>
) {
  if (import.meta.env.MODE === "development") {
    console.log("Sentry message in development mode (disabled):", message);
    return;
  }

  Sentry.withScope((scope) => {
    if (context) {
      scope.setContext("additional_info", context);
    }
    Sentry.captureMessage(message, level);
  });
}

// Helper function to set custom tags
export function setSentryTag(key: string, value: string) {
  if (import.meta.env.MODE === "development") {
    console.log("Sentry tag in development mode (disabled):", key, value);
    return;
  }

  Sentry.setTag(key, value);
}

// Helper function to set custom context
export function setSentryContext(key: string, context: Record<string, any>) {
  if (import.meta.env.MODE === "development") {
    console.log("Sentry context in development mode (disabled):", key, context);
    return;
  }

  Sentry.setContext(key, context);
}
